import * as GlobalS from "@/components/styled";
import { useDebounce, useSortableTable } from "@/hooks";
import { useSearchPermissionsQuery } from "@/services";
import { Button, Space, Table } from "antd";
import {
  array,
  arrayOf,
  bool,
  element,
  func,
  object,
  shape,
  string,
} from "prop-types";
import { useEffect, useState } from "react";
import { StringParam, useQueryParam } from "use-query-params";
import { PageWrapper } from "../PageWrapper";
import { SearchBox } from "../SearchBox";

const PermissionsList = ({
  stateKey,
  columns,
  additionalQueryStringParams,
  searchWithNoQuery,
  showAdvanced = false,
  showFilter = false,
  advancedSearchFields = {},
  customActions = [],
  searchPlaceholder,
  searchHelpText,
  pageTitle,
  permissions,
  setPermissions,
}) => {
  const [query, setQuery] = useQueryParam("query", StringParam);
  const [searchTerm, setSearchTerm] = useState(query || "");
  const [filter, setFilter] = useQueryParam("filter", StringParam);
  const [searchFilter] = useState(filter || "");
  const [orderBy] = useSortableTable(columns);
  const page = 1;

  const debouncedSearchFilter = useDebounce(searchFilter, 1000);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [searchParams, setSearchParams] = useState(query);
  const { isFetching: isLoading, data: searchData } =
    useSearchPermissionsQuery(searchParams);

  useEffect(() => {
    setPermissions(searchData);
  }, [searchData, setPermissions]);

  // When the query string parameters change or orderBy filters change, do another search
  useEffect(() => {
    if (window.location.search !== "" || searchWithNoQuery) {
      setSearchParams({
        query,
        orderBy,
        filter,
        page,
        ...additionalQueryStringParams,
      });
    }
  }, [additionalQueryStringParams, filter, orderBy, query, searchWithNoQuery]);

  // When the search term changes (throttled), refresh the query parameters
  useEffect(() => {
    setQuery(debouncedSearchTerm, "pushIn");
  }, [debouncedSearchTerm, setQuery]);

  // When the search filter changes (throttled), refresh to query parameters
  useEffect(() => {
    setFilter(debouncedSearchFilter, "pushIn");
  }, [debouncedSearchFilter, setFilter]);

  const searchBoxConfig = {
    showFilter: showFilter,
    showAdvanced: showAdvanced,
    searchFieldPlaceholder: searchPlaceholder,
    searchTerm: searchTerm,
    setSearchTerm: setSearchTerm,
    isLoading: isLoading,
    helpText: searchHelpText,
    advancedSearchFields: advancedSearchFields,
    key: stateKey,
  };

  return (
    <PageWrapper pageTitle={pageTitle}>
      <SearchBox config={searchBoxConfig} />
      <GlobalS.PageContentCard
        title={pageTitle}
        extra={
          <Space>
            {customActions?.map(({ label, icon, onClick }) => (
              <Button
                data-test-id={`search-page-${label}-button`}
                type="primary"
                key={`action-${label}`}
                icon={icon}
                onClick={onClick}
              >
                {label}
              </Button>
            ))}
          </Space>
        }
      >
        <Table
          rowKey={({ id }) => id}
          pagination={true}
          loading={isLoading}
          columns={columns}
          dataSource={permissions}
        />
      </GlobalS.PageContentCard>
    </PageWrapper>
  );
};

PermissionsList.propTypes = {
  stateKey: string.isRequired,
  columns: array.isRequired,
  additionalQueryStringParams: object,
  searchWithNoQuery: bool,
  showAdvanced: bool,
  showFilter: bool,
  advancedSearchFields: object,
  customActions: arrayOf(
    shape({
      label: string,
      icon: element,
      onClick: func,
    })
  ),
  searchPlaceholder: string,
  searchHelpText: string,
  pageTitle: element.isRequired,
  permissions: arrayOf(object),
  setPermissions: func.isRequired,
};

export default PermissionsList;
